<template>
  <form @submit.prevent="handleSubmit" class="stf-form">
    <h5 class="stf-title">Limited Company contact details</h5>

    <!-- Name -->
    <div class="stf-row">
      <input
        type="text"
        name="comp_name"
        v-model="form.companyname"
        required
        class="stf-field"
        placeholder="Name/Company Name*"
      />
      <span v-if="errors.companyname" class="text-danger">{{ errors.companyname }}</span>
    </div>

    <!-- Business category -->
    <div class="stf-row">
      <div class="stf-select-wrap">
        <select
          v-model="form.business_category_id"
          required
          class="stf-field"
          :class="{ 'has-value': form.business_category_id }"
        >
          <option disabled selected value="">Business Category*</option>
          <option
            v-for="option in business_category_options"
            :value="option.id"
            :key="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <span v-if="errors.business_category_id" class="text-danger">{{
        errors.business_category_id
      }}</span>
    </div>

    <!-- Contact fields + logo -->
    <div class="stf-row stf-contact-cluster">
      <div class="stf-contact-fields">
        <div>
          <input
            type="tel"
            v-model="form.mobile"
            @input="formatMobile"
            required
            class="stf-field"
            placeholder="Mobile no*"
          />
          <span v-if="errors.mobile" class="text-danger">{{ errors.mobile }}</span>
        </div>
        <div>
          <input
            type="tel"
            v-model="form.phone1"
            @input="formatPhone"
            class="stf-field"
            placeholder="Phone no"
          />
          <span v-if="errors.phone1" class="text-danger">{{ errors.phone1 }}</span>
        </div>
      </div>

      <div class="stf-logo-box-wrap">
        <label
          for="logo-file"
          class="stf-logo-box"
          :aria-label="imagePreview ? 'Change logo' : 'Upload logo'"
        >
          <template v-if="imagePreview">
            <img class="stf-logo-img" :src="imagePreview" alt="Uploaded logo preview" />
            <button
              class="stf-logo-remove"
              type="button"
              aria-label="Remove logo"
              @click.stop.prevent="removeLogo"
            >&times;</button>
          </template>
          <span v-else class="stf-logo-plus" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 3.5 V16.5 M3.5 10 H16.5" stroke="#d9642c"
                    stroke-width="2.5" stroke-linecap="round" />
            </svg>
          </span>
        </label>

        <input
          type="file"
          id="logo-file"
          ref="uploads"
          accept="image/*"
          class="stf-visually-hidden"
          @change="handleFileSelect($event)"
        />
        <span class="stf-logo-caption">Upload Logo</span>
      </div>
    </div>

    <!-- Collapse toggle -->
    <div class="stf-collapse-toggle">
      <button
        class="stf-chevron-btn"
        :class="{ collapsed: !addressExpanded }"
        type="button"
        :aria-expanded="addressExpanded ? 'true' : 'false'"
        :aria-label="addressExpanded ? 'Collapse address section' : 'Expand address section'"
        @click="addressExpanded = !addressExpanded"
      >
        <svg width="24" height="14" viewBox="0 0 24 14" fill="none">
          <path d="M2 12 L12 3 L22 12" stroke="#e0a12e" stroke-width="2.5"
                stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>

    <!-- Address section -->
    <div class="stf-address-section" v-show="addressExpanded">
      <div>
        <input
          type="text"
          v-model="form.postal_address"
          class="stf-field"
          placeholder="Postal address / Street 1"
        />
        <span v-if="errors.postal_address" class="text-danger">{{
          errors.postal_address
        }}</span>
      </div>

      <div>
        <input
          type="text"
          v-model="form.street2"
          class="stf-field"
          placeholder="Street 2"
        />
        <span v-if="errors.street2" class="text-danger">{{ errors.street2 }}</span>
      </div>

      <div class="stf-split-row">
        <div class="stf-col-wide">
          <div class="stf-select-wrap">
            <select
              v-model="form.state"
              class="stf-field"
              :class="{ 'has-value': form.state }"
            >
              <option selected disabled value="">County/State</option>
              <option v-for="option in states" :value="option.name" :key="option.id">
                {{ option.name }}
              </option>
            </select>
          </div>
          <span v-if="errors.state" class="text-danger">{{ errors.state }}</span>
        </div>
        <div class="stf-col-narrow">
          <input
            type="text"
            v-model="form.zip"
            class="stf-field"
            placeholder="Zip/Postal Code"
          />
          <span v-if="errors.zip" class="text-danger">{{ errors.zip }}</span>
        </div>
      </div>

      <div>
        <input
          type="text"
          v-model="form.website"
          class="stf-field"
          placeholder="Website"
        />
        <span v-if="errors.website" class="text-danger">{{ errors.website }}</span>
      </div>
    </div>

    <!-- Footer -->
    <div class="stf-footer">
      <button type="submit" role="button" :disabled="loading" class="stf-btn-next">
        Next
      </button>
    </div>
  </form>
</template>
<script>
import { BUSINESS_CATEGORIES, COUNTRIES_LIST } from "../../config/url";

export default {
  name: "SoleTraderFormCompany",
  data() {
    return {
      loadingInstance: null,
      loading: false,
      countries: null,
      business_categories: null,
      states: "",
      imagePreview: "",
      addressExpanded: true,
      form: {
        fullname: "",
        business_category_id: "",
        postal_address: "",
        street2: "",
        state: "",
        zip: "",
        phone1: "",
        mobile: "",
        website: "",
        companyname: "",
        companytype: "Limited Company",
        image: "",
        is_logo: "N",
      },
      errors: {},
    };
  },
  mounted() {
    let user_details_local = this.$store.getters["auth/getUserData"];
    this.country_id = user_details_local.country_id;
    this.form.fullname = user_details_local.welcomename;
    this.$nextTick(() => {
      this.fetchBusinessCategories();
      this.fetchStateByCountry();
    });
  },
  computed: {
    pageTitle() {
      return (document.title = `${this.title.replace(/\b\w/g, (match) =>
        match.toUpperCase()
      )} | Smoothbalance`);
    },
    country_options() {
      if (this.countries) {
        return this.countries.map((v) => {
          return {
            id: v.id,
            name: v.name,
          };
        });
      }
      return [];
    },
    business_category_options() {
      if (this.business_categories) {
        return this.business_categories.map((v) => ({ id: v.id, name: v.name }));
      }
      return [];
    },
  },
  methods: {
    formatMobile() {
      if (this.form.mobile) {
        this.form.mobile = this.form.mobile.replace(/[a-zA-Z]/g, "");
      }
    },
    formatPhone() {
      if (this.form.phone1) {
        this.form.phone1 = this.form.phone1.replace(/[a-zA-Z]/g, "");
      }
    },
    showLoading() {
      this.loadingInstance = this.$loading({
        lock: true,
        text: "Loading",
        background: "rgba(0, 0, 0, 0.7)",
        fullscreen: true,
      });
    },
    hideLoading() {
      this.loadingInstance.close();
    },
    async fetchBusinessCategories() {
      this.business_categories = await this.axios
        .get(BUSINESS_CATEGORIES)
        .then((res) => res.data.message)
        .catch((err) => console.log(err));
    },
    async fetchCountries() {
      this.countries = await this.axios
        .get(COUNTRIES_LIST)
        .then(function (res) {
          return res.data.response;
        })
        .catch((err) => {
          console.log(err);
        });
    },
    fetchStateByCountry() {
      this.showLoading();

      this.$store
        .dispatch("client/fetchStateByCountry", { country_id: this.country_id })
        .then((res) => {
          if (res.status == 1) {
            this.states = res.response;
          } else {
            throw new Error(res);
          }
        })
        .catch((err) => {
          console.log(err);
          this.$toast.error("Something went wrong. Please try again.");
        })
        .finally(() => {
          this.hideLoading();
        });
    },
    validateForm() {
      this.errors = {}; // clear old errors

      if (!this.form.companyname) this.errors.companyname = "Company name is required";
      if (!this.form.business_category_id)
        this.errors.business_category_id = "Business category is required";
      if (!this.form.mobile) this.errors.mobile = "Mobile number is required";
      if (!this.form.phone1) this.errors.phone1 = "Phone number is required";
      if (!this.form.postal_address)
        this.errors.postal_address = "Postal address is required";
      if (!this.form.street2) this.errors.street2 = "Street name is required";
      if (!this.form.state) this.errors.state = "State is required";
      if (!this.form.zip) this.errors.zip = "Zip code is required";
      // optional website validation:
      // if (!this.form.website) this.errors.website = "Website is required";

      return Object.keys(this.errors).length === 0;
    },
    handleSubmit() {
      if (!this.validateForm()) {
        this.$toast.error("Please fill in all required fields.");
        return;
      }

      this.showLoading();

      let formData = new FormData();

      // Append all form fields
      for (const key in this.form) {
        formData.append(key, this.form[key]);
      }

      this.$store
        .dispatch("company/save", formData)
        .then((res) => {
          if (res.status == 1) {
            this.$confirm = this.$alert || this.$msgbox?.alert; // backward compatibility safeguard

            this.$alert(
              "This session will close now. Please log in, and your profile will be completed.",
              "Session Completed",
              {
                confirmButtonText: "OK",
                type: "success",
                showClose: false,
                closeOnClickModal: false,
                closeOnPressEscape: false,
              }
            ).then(() => {
              // 🔹 When user clicks OK → logout and redirect
              this.$store.dispatch("auth/logout").then(() => {
                this.$router.push({ path: "/logout" });
              });
            });
          } else {
            throw new Error(res);
          }
        })
        .catch((err) => {
          console.log(err);
          this.$toast.error("Something went wrong. Please try again.");
        })
        .finally(() => {
          this.hideLoading();
        });
    },
    handleFileSelect(event) {
      const file = event.target.files[0];
      if (!file) return;

      const maxSize = 128 * 1024; // 128 KB
      const maxDimension = 128; // max width/height for a logo (smaller than regular images)

      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;

        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          let { width, height } = img;

          // Resize proportionally
          if (width > height) {
            if (width > maxDimension) {
              height *= maxDimension / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width *= maxDimension / height;
              height = maxDimension;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Compress to roughly 128 KB
          let quality = 0.9;
          const qualityStep = 0.05;

          const compress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size > maxSize && quality > 0.1) {
                  quality -= qualityStep;
                  compress();
                } else {
                  // Set the form value and preview
                  this.form.image = new File([blob], file.name, { type: blob.type });
                  this.form.is_logo = "Y";
                  this.imagePreview = URL.createObjectURL(blob);
                }
              },
              "image/jpeg",
              quality
            );
          };

          compress();
        };

        img.onerror = (err) => {
          console.error("Error loading image:", err);
          this.$toast.error("Failed to process the logo image.");
        };
      };

      reader.onerror = (err) => {
        console.error("FileReader error:", err);
        this.$toast.error("Failed to read the file.");
      };
    },
    removeLogo() {
      this.imagePreview = "";
      this.form.image = "";
      this.form.is_logo = "N";
      if (this.$refs.uploads) this.$refs.uploads.value = "";
    },
  },
};
</script>
<style scoped>
.stf-form {
  --stf-primary: #008490;
  --stf-primary-dark: #006b75;
  --stf-remove-bg: rgba(0, 132, 144, 0.9);
  display: flex;
  flex-direction: column;
  flex: 1;
}

.stf-title {
  font-size: 19px;
  font-weight: 700;
  color: #1f2a35;
  margin: 6px 0 26px 8px;
}

.stf-row {
  margin-bottom: 16px;
}

.stf-field {
  width: 100%;
  height: 46px;
  padding: 0 16px;
  font-size: 14px;
  color: #2b2f33;
  background: #fff;
  border: 1px solid #d5d8dc;
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s ease;
}
.stf-field::placeholder {
  color: #9aa0a6;
}
.stf-field:focus {
  border-color: var(--stf-primary);
  box-shadow: none;
}

/* Select wrapper with custom chevron */
.stf-select-wrap {
  position: relative;
}
select.stf-field {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: #9aa0a6;
  cursor: pointer;
}
select.stf-field.has-value {
  color: #2b2f33;
}
.stf-select-wrap::after {
  content: "";
  position: absolute;
  right: 16px;
  top: 50%;
  width: 9px;
  height: 9px;
  border-right: 2px solid var(--stf-primary);
  border-bottom: 2px solid var(--stf-primary);
  transform: translateY(-70%) rotate(45deg);
  pointer-events: none;
}

/* Contact + logo cluster */
.stf-contact-cluster {
  display: flex;
  gap: 18px;
  align-items: flex-start;
}
.stf-contact-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

/* Logo box */
.stf-logo-box-wrap {
  width: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
}
.stf-logo-box {
  position: relative;
  width: 96px;
  height: 96px;
  border: 1px solid var(--stf-primary);
  border-radius: 8px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  overflow: hidden;
  margin: 0;
}
.stf-logo-plus {
  width: 40px;
  height: 40px;
  border: 1px solid var(--stf-primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.stf-logo-plus svg {
  display: block;
}
.stf-logo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.stf-logo-remove {
  position: absolute;
  top: 3px;
  right: 3px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: var(--stf-remove-bg);
  color: #fff;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.stf-logo-remove:hover {
  background: var(--stf-primary-dark);
}
.stf-logo-caption {
  margin-top: 8px;
  font-size: 14px;
  color: #1f2a35;
  text-align: center;
}
.stf-visually-hidden {
  display: none;
}

/* Collapse toggle */
.stf-collapse-toggle {
  display: flex;
  justify-content: center;
  margin: 20px 0 18px;
}
.stf-chevron-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 12px;
  line-height: 0;
}
.stf-chevron-btn svg {
  display: block;
  transition: transform 0.2s ease;
}
.stf-chevron-btn.collapsed svg {
  transform: rotate(180deg);
}

/* Address grid */
.stf-address-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.stf-split-row {
  display: flex;
  gap: 16px;
}
.stf-split-row .stf-col-wide {
  flex: 1.15;
  min-width: 0;
}
.stf-split-row .stf-col-narrow {
  flex: 0.85;
  min-width: 0;
}

/* Footer */
.stf-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 24px;
}
.stf-btn-next {
  background: var(--stf-primary);
  color: #fff;
  font-weight: 700;
  font-size: 15px;
  border: none;
  border-radius: 6px;
  padding: 14px 40px;
  cursor: pointer;
  transition: background 0.15s ease;
}
.stf-btn-next:hover {
  background: var(--stf-primary-dark);
}
.stf-btn-next:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.text-danger {
  font-size: 13px;
}

@media (max-width: 520px) {
  .stf-contact-cluster {
    flex-direction: column-reverse;
    align-items: center;
  }
  .stf-contact-fields {
    width: 100%;
  }
  .stf-split-row {
    flex-direction: column;
  }
}
</style>
